import { memo, useCallback, useMemo, useState } from "react";

import CollectionsBookmarkIcon from "@mui/icons-material/CollectionsBookmark";
import { Button, Stack, SxProps } from "@mui/material";

import TextField from "@/components/TextField";
import emoticons from "@/components/TextField/emoticons";

import PoemLibraryDialog from "@/extensions/obituary/components/PoemLibraryDialog";
import {
  DEBOUNCE,
  Editable,
  Item,
} from "@/extensions/obituary/pages/detail/Story/shared";
import { Poem } from "@/extensions/obituary/types";

import { useStory } from "../context";

import { makeRelativesColumn } from "./Column";
import { Container } from "./Container";
import EditableItem from "./EditableItem";
import { makeSingleline } from "./Singleline";

import * as object from "@/utils/object";
import { Nullish, Option } from "@/utils/types";

const styles: Record<string, SxProps> = {
  button: {
    justifyContent: "flex-start",
    gap: 0.5,
  },
};

export type Variant =
  | "greeting"
  | "epitaph"
  | "poem"
  | "funeral"
  | "farewell"
  | "ending"
  | RelativeVariant;

export type RelativeVariant =
  | "mourners"
  | "husband-wife"
  | "child"
  | "grandchild"
  | "greatgrandchild";

export interface Multiline extends Item {
  type: "multiline";
  variant?: Variant;
  text: Nullish<string>;
}

export function isMultiline(item: Item): item is Multiline {
  return item.type === "multiline";
}

export const variants: { value: Variant; label: string }[] = [
  { value: "greeting", label: "Hälsning" },
  { value: "epitaph", label: "Text" },
  { value: "poem", label: "Vers" },
  { value: "funeral", label: "Begravningsinformation" },
  { value: "farewell", label: "Avslut" },
  { value: "mourners", label: "Sörjande" },
  { value: "husband-wife", label: "Livspartner" },
  { value: "child", label: "Barn" },
  { value: "grandchild", label: "Barnbarn" },
  { value: "greatgrandchild", label: "Barnbarnsbarn" },
];

export function isVariant(value: unknown): value is Variant {
  return variants.some((variant) => variant.value === value);
}

export function makeMultiline(multiLine: Partial<Multiline>): Multiline {
  return {
    id: crypto.randomUUID(),
    type: "multiline",
    text: "",
    ...multiLine,
  };
}

/** Variants used in `relatives` container. */
export const relativeVariants: Variant[] = [
  "mourners",
  "husband-wife",
  "child",
  "grandchild",
  "greatgrandchild",
];

/** Options for selecting variant in `relatives` container. */
export const relativeVariantOptions: Option[] = variants.filter((option) =>
  relativeVariants.includes(option.value),
);

const Edit: Editable<Multiline> = ({
  item: { hidden, text, variant },
  path,
}) => {
  const { change, getContainerVariants, layout } = useStory();

  // Used to reset the uncontrolled text field when a poem is selected.
  const [textFieldKey, setTextFieldKey] = useState(0);
  const [openPoemDialog, setOpenPoemDialog] = useState(false);

  const isPoem = variant === "poem";

  const isRelative = useMemo(() => {
    const [root] = getContainerVariants(path);
    return root === "relatives";
  }, [getContainerVariants, path]);

  const title = useMemo(() => {
    let title =
      variants.find((option) => option.value === variant)?.label ?? "Fritext";
    if (hidden) title = `${title} (Dold)`;
    return title;
  }, [hidden, variant]);

  /// Callbacks

  const handleChangeVariant = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      const variant = event.target.value;
      if (isVariant(variant)) change<Multiline>(path, { variant });
      else console.error("Invalid variant", variant);
    },
    [change],
  );

  const variantProps = useMemo(() => {
    return !isRelative
      ? {}
      : {
          variant,
          variants: relativeVariantOptions,
          onVariantChange: handleChangeVariant,
        };
  }, [isRelative, variant, handleChangeVariant]);

  const handleChangeText = useCallback(
    (text: string) => change<Multiline>(path, { text }),
    [change],
  );

  const handleSplit = useMemo(
    () =>
      layout === "OBITUARY_TWO_COLUMN" && isRelative
        ? () =>
            change<Container>(path, {
              type: "container",
              flex: "row",
              variant: "relatives-row",
              id: crypto.randomUUID(),
              children: [
                makeRelativesColumn(
                  makeSingleline({
                    text: text ?? "",
                    variant: variant as RelativeVariant,
                  }),
                ),
                makeRelativesColumn({ variant: variant as RelativeVariant }),
              ],
            })
        : null,
    [change, text, variant, isRelative, layout],
  );

  const handleOpenPoemDialog = useCallback(() => setOpenPoemDialog(true), []);
  const handleClosePoemDialog = useCallback(() => setOpenPoemDialog(false), []);

  const handleSelectPoem = useCallback(
    ({ text, author }: Poem) => {
      if (!isPoem) return;

      change<Multiline>(path, object.omitNullish({ text }));

      // We have to find and set the author manually, as it is rendered by another component.
      // XXX: _Massive_ assumption, requires the author to be the next item in the container.
      //      This _will_ break if you change the `template` structure.
      if (author != null) {
        const tail = path.length - 1;
        const authorPath = [...path];

        if (typeof authorPath[tail] === "number") {
          authorPath[tail] += 1;
          change<Multiline>(authorPath, { text: author });
        } else {
          console.error("Invalid path to author", authorPath);
        }
      }

      setTextFieldKey((prev) => prev + 1);
      handleClosePoemDialog();
    },
    [change],
  );

  return (
    <EditableItem
      actions={isRelative ? "mutate" : "hide"}
      hidden={hidden}
      path={path}
      title={title}
      onSplit={handleSplit}
      {...variantProps}
    >
      {!hidden && (
        <Stack gap={1}>
          <TextField
            key={textFieldKey}
            closeEmoticonPickerOnSelect
            multiline
            debounce={DEBOUNCE}
            disabled={hidden}
            emoticons={emoticons}
            minRows={isRelative ? 1 : isPoem ? 5 : 3}
            name="text"
            value={text ?? ""}
            variant="outlined"
            onChange={handleChangeText}
          />

          {isPoem && (
            <Button
              disableRipple
              startIcon={<CollectionsBookmarkIcon />}
              sx={styles.button}
              onClick={handleOpenPoemDialog}
            >
              Välj vers från bibliotek
            </Button>
          )}
        </Stack>
      )}

      {isPoem && openPoemDialog && (
        <PoemLibraryDialog
          open={openPoemDialog}
          onClose={handleClosePoemDialog}
          onSubmit={handleSelectPoem}
        />
      )}
    </EditableItem>
  );
};

export default memo(Edit);
